import { useCallback, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import {
  Button,
  Card,
  CUSTOMER_SEAT_AVAILABILITY_STATES,
  EmptyState,
  SEAT_ACCESSIBILITY_PROVISIONS,
  SEAT_FEATURES,
  LoadingRegion,
  Notice,
  formatMoney,
  presentation,
} from '@gatecheckpro/design-system';
import {
  NO_OVERLAY,
  VenueMapCanvas,
  batchOccurrences,
  indexSeatDetail,
  roomTopologyOfCustomer,
  seatAppearances,
  sectionAppearances,
} from '@gatecheckpro/venue-map';
import {
  SEAT_DETAIL_MAX,
  type CustomerSeatingView,
  type CustomerTopology,
  type Order,
  type SeatHold,
  type SeatOption,
  type SeatOptionsResponse,
  type TableOption,
} from '@gatecheckpro/contracts';
import { ApiRefusal } from '@gatecheckpro/api-client';
import { api } from '../lib/api.ts';
import { useResource } from '../lib/useResource.ts';
import { useCommand } from '../lib/useCommand.ts';
import { useNowTicking } from '../lib/useCountdown.ts';
import { useSession } from '../lib/session.tsx';
import { FailureNotice } from '../lib/refusal.tsx';
import {
  NOTHING_SELECTED,
  acquisitionSelection,
  isRequestable,
  isSeatSelected,
  selectWholeTable,
  selectedSeatCount,
  toggleSeat,
  whyNotChoosable,
  type SeatSelection,
} from '../lib/seating/selection.ts';
import { holdClock } from '../lib/seating/hold-clock.ts';

/**
 * Choosing a seat, and then actually getting it.
 *
 * The page is deliberately readable without an account. Somebody deciding
 * whether to come to a show should be able to see what the room looks like and
 * what the good seats cost, and being asked to register first is how a ticketing
 * site loses the person before it has told them anything.
 *
 * What that does *not* mean is that anything is theirs while they browse.
 * Selection here is local intent and nothing more: no chair is reserved, two
 * people can be looking at the same seat with it highlighted on both screens,
 * and exactly one of them will get it. The moment that matters is the hold, and
 * the hold is the server's to grant.
 */
export function SeatSelectionPage() {
  const { slug, sessionId } = useParams<{ slug: string; sessionId: string }>();
  const session = useSession();
  const navigate = useNavigate();

  const base = `/api/v1/customer/event-sessions/${sessionId ?? ''}/seating`;
  const view = useResource<CustomerSeatingView>(sessionId === undefined ? null : base);
  const topology = useResource<CustomerTopology>(
    sessionId === undefined ? null : `${base}/topology`,
  );

  /*
   * What was chosen, including whatever survived a trip to the sign-in page.
   *
   * Read once, on mount, and cleared as it is read. Restored as *intent only*:
   * nothing was reserved while they were away — there is no anonymous hold — so
   * this is the same local choice they made, which the server may now refuse.
   * Deliberately not turned straight into a hold request either. Taking chairs
   * on somebody's behalf because they arrived back at a page is not something
   * they asked for.
   */
  const stash = `gatecheck.seats.${sessionId ?? ''}`;
  /**
   * Which physical facts a customer is looking for.
   *
   * Presentation, and only presentation. Nothing here changes what is for sale
   * or what may be held; it changes which chairs are easy to find.
   */
  const [wanted, setWanted] = useState<{ accessible: boolean; unobstructed: boolean }>({
    accessible: false,
    unobstructed: false,
  });

  const [selection, setSelection] = useState<SeatSelection>(() => {
    const remembered = window.sessionStorage.getItem(stash);
    if (remembered === null) return NOTHING_SELECTED;
    window.sessionStorage.removeItem(stash);
    try {
      return JSON.parse(remembered) as SeatSelection;
    } catch {
      // A selection that will not parse is one nobody can act on. Losing it
      // silently beats restoring something half-read.
      return NOTHING_SELECTED;
    }
  });
  const [hold, setHold] = useState<SeatHold | null>(null);
  // Ticking whether or not there is a hold to count down. A hook cannot live
  // behind a condition, and the alternative — reading it only when a hold
  // exists — is a rule-of-hooks violation waiting for the render that has one.
  const now = useNowTicking();
  const holdCommand = useCommand<SeatHold>();
  const checkout = useCommand<Order>();

  /**
   * What the server says about the chairs on screen, and only those.
   *
   * The same bounded ask the organizer's room makes, for the same reason: at a
   * hundred thousand chairs the whole room's state is megabytes nobody can use,
   * and the renderer already knows which few hundred are visible.
   */
  const [options, setOptions] = useState<readonly SeatOption[]>([]);
  const [tables, setTables] = useState<readonly TableOption[]>([]);
  const asked = useRef('');

  const requestOptions = useCallback(
    (occurrences: readonly string[], configurationVersionId: string) => {
      const key = occurrences.join(' ');
      if (key === asked.current) return;
      asked.current = key;
      if (occurrences.length === 0) {
        setOptions([]);
        setTables([]);
        return;
      }
      void Promise.all(
        batchOccurrences(occurrences, SEAT_DETAIL_MAX).map((batch) =>
          api.request<SeatOptionsResponse>(`${base}/seat-options`, {
            method: 'POST',
            body: {
              seatingConfigurationVersionId: configurationVersionId,
              venueMapSeatIds: batch,
            },
          }),
        ),
      )
        .then((responses) => {
          // An answer to a question nobody is asking any more: the person has
          // panned or zoomed since, and painting it would dress the chairs now
          // on screen with the state of the ones that were.
          if (asked.current !== key) return;
          setOptions(responses.flatMap((response) => response.seats));
          setTables(responses.flatMap((response) => response.tables));
        })
        .catch(() => {
          setOptions([]);
          setTables([]);
        });
    },
    [base],
  );

  if (view.resource.status === 'loading' || topology.resource.status === 'loading') {
    return <LoadingRegion label="Loading the room" />;
  }
  if (view.resource.status === 'failed') {
    return (
      <main className="gc-main">
        <div className="gc-container gc-stack">
          <EmptyState title="This date does not have reserved seating">
            Seats are not chosen individually for this one. Go back to the event to see what is on
            sale.
          </EmptyState>
          <Link to={`/e/${slug ?? ''}`}>Back to the event</Link>
        </div>
      </main>
    );
  }
  if (topology.resource.status === 'failed') {
    return <FailureNotice error={topology.resource.error} />;
  }

  const seating = view.resource.data;
  const room = topology.resource.data;
  const drawing = roomTopologyOfCustomer(room);
  const optionByOccurrence = new Map(options.map((option) => [option.venueMapSeatId, option]));
  const overlay = options.length === 0 ? NO_OVERLAY : indexSeatDetail(options);

  const chosen = options.filter((option) => isSeatSelected(selection, option.venueMapSeatId));
  const total = chosen.reduce((sum, option) => sum + (option.price?.minor ?? 0), 0);
  const currency = chosen[0]?.price?.currency ?? 'NGN';
  const wholeTable =
    selection.kind === 'WHOLE_TABLE'
      ? (tables.find((table) => table.venueMapTableId === selection.venueMapTableId) ?? null)
      : null;

  /**
   * Asks for the hold, once there is somebody to hold it for.
   *
   * An anonymous visitor is sent to sign in with their selection remembered and
   * this page named as where to come back to. Nothing is reserved while they
   * are away — there is no such thing as an anonymous hold — so on their return
   * the chairs are asked for fresh, and may be gone.
   */
  async function requestHold(): Promise<void> {
    const wanted = acquisitionSelection(selection);
    if (wanted === null) return;

    if (session.state.status !== 'authenticated') {
      window.sessionStorage.setItem(stash, JSON.stringify(wanted));
      void navigate(`/sign-in?next=${encodeURIComponent(window.location.pathname)}`);
      return;
    }

    const granted = await holdCommand.run(`${base}/seat-holds`, {
      method: 'POST',
      body: {
        seatingConfigurationVersionId: seating.seatingConfigurationVersionId,
        selection: wanted,
      },
    });
    if (granted !== null) setHold(granted);
  }

  /*
   * Which chairs went, read from the refusal itself.
   *
   * Not from state written when the command failed: `run` returns before React
   * has re-rendered with the new error, so reading `holdCommand.error` straight
   * after awaiting it would see the previous one — which is `null` the first
   * time, and the page would silently show nothing.
   *
   * The server never grants part of a request — four chairs asked for is four
   * or none — so this acquisition failed outright. Naming the ones that are
   * gone lets the page mark exactly those and leave the rest selected, but what
   * is left is a *new* choice the person makes, not a smaller version of the
   * one that failed.
   */
  const lost =
    holdCommand.error instanceof ApiRefusal &&
    holdCommand.error.reasonCode === 'SEAT_SELECTION_UNAVAILABLE'
      ? ((holdCommand.error.details?.venueMapSeatIds as string[] | undefined) ?? [])
      : [];

  return (
    <main className="gc-main">
      <div className="gc-container gc-stack gc-stack--loose" style={{ maxWidth: '64rem' }}>
        <div className="gc-stack gc-stack--tight">
          <Link to={`/e/${slug ?? ''}`}>← Back to the event</Link>
          <h1>Choose your seats</h1>
          <p className="gc-muted" data-testid="room-counts">
            {seating.summary.configuration.available.toLocaleString()} available of{' '}
            {seating.summary.configuration.sellable.toLocaleString()} on sale
          </p>
        </div>

        {hold !== null ? (
          <Card
            title="These seats are being held for you"
            eyebrow={holdClock(hold.expiresAt, now).remaining}
          >
            {/*
              The hold's own identifier, on the element that represents it. It
              is the thing checkout is about to name, and a surface that shows a
              hold without being able to say which one is harder to reason about
              from the outside than it needs to be.
            */}
            <div className="gc-stack" data-seat-hold={hold.seatHoldId}>
              <p>{holdClock(hold.expiresAt, now).meaning}</p>
              <ul className="gc-stack gc-stack--tight" data-testid="held-seats">
                {hold.seats.map((seat) => (
                  <li key={seat.venueMapSeatId}>{seat.seatLabel}</li>
                ))}
              </ul>
              <p className="gc-mono">
                {hold.productName} · {formatMoney(hold.totalPrice)}
              </p>
              <FailureNotice error={checkout.error} />
              <Button
                variant="primary"
                busy={checkout.busy}
                onClick={() => {
                  void checkout
                    .run('/api/v1/customer/checkout', {
                      method: 'POST',
                      // The hold, and nothing else. Product, quantity and price
                      // are already written on it, and the contract refuses a
                      // body that tries to say them again.
                      body: { seatHoldId: hold.seatHoldId },
                      idempotencyKey: `seat-checkout:${hold.seatHoldId}`,
                    })
                    .then((order) => {
                      if (order !== null) void navigate(`/orders/${order.orderId}`);
                    });
                }}
              >
                Check out
              </Button>
            </div>
          </Card>
        ) : (
          <Card
            title="The room"
            eyebrow={
              selection.kind === 'WHOLE_TABLE'
                ? (wholeTable?.tableName ?? 'A table')
                : `${String(selectedSeatCount(selection))} selected`
            }
            action={
              <Button
                variant="primary"
                busy={holdCommand.busy}
                disabled={!isRequestable(selection)}
                onClick={() => void requestHold()}
              >
                {session.state.status === 'authenticated' ? 'Hold these seats' : 'Sign in to hold'}
              </Button>
            }
          >
            <div className="gc-stack">
              {lost.length > 0 && (
                <Notice tone="caution" title="Some of those seats went" live>
                  Somebody was quicker. Nothing was charged and nothing is held. The seats still
                  free are still selected — choose again when you are ready.
                </Notice>
              )}
              <FailureNotice error={holdCommand.error} />

              {chosen.length > 0 && (
                <p className="gc-mono" data-testid="selection-total">
                  {chosen.length} seat{chosen.length === 1 ? '' : 's'} ·{' '}
                  {formatMoney({ minor: total, currency })}
                </p>
              )}
              {wholeTable !== null && wholeTable.totalPrice !== null && (
                <p className="gc-mono" data-testid="selection-total">
                  {wholeTable.tableName} · {String(wholeTable.seatCount)} seats ·{' '}
                  {formatMoney(wholeTable.totalPrice)}
                </p>
              )}

              {/*
                Two facts, offered separately, because they are separate
                questions. Somebody who needs step-free access and somebody who
                minds a pillar are not the same person, and one control for
                both would serve neither.

                Nothing here asks a customer to declare anything about
                themselves. GateCheckPro holds no evidence and applies no
                eligibility rule: the organizer marked the chair, and it is
                bought like any other.
              */}
              <fieldset className="gc-fieldset" data-testid="seat-features">
                <legend className="gc-field__label">Looking for something in particular?</legend>
                <label className="gc-checkbox">
                  <input
                    type="checkbox"
                    checked={wanted.accessible}
                    onChange={(event) =>
                      setWanted((current) => ({ ...current, accessible: event.target.checked }))
                    }
                  />
                  Point out accessible seats
                </label>
                <label className="gc-checkbox">
                  <input
                    type="checkbox"
                    checked={wanted.unobstructed}
                    onChange={(event) =>
                      setWanted((current) => ({ ...current, unobstructed: event.target.checked }))
                    }
                  />
                  Point out seats with a full view
                </label>
                <p className="gc-muted" data-testid="seat-feature-counts">
                  {room.seats.filter((seat) => seat.isAccessible).length} accessible ·{' '}
                  {room.seats.filter((seat) => seat.hasObstructedView).length} with a restricted
                  view
                </p>
              </fieldset>

              <VenueMapCanvas
                topology={drawing}
                announceWith="displayName"
                seatOverlay={dressWithFeatures(
                  room,
                  wanted,
                  seatAppearances(
                    room.seats.map((seat) => ({
                      seatId: seat.venueMapSeatId,
                      seatKey: seat.venueMapSeatId,
                    })),
                    overlay,
                    CUSTOMER_SEAT_AVAILABILITY_STATES,
                  ),
                )}
                sectionOverlay={sectionAppearances(
                  room.sections.map((section) => ({
                    sectionId: section.sectionId,
                    code: section.sectionId,
                  })),
                  seating.summary,
                )}
                onDrawnSeatsChange={(seatKeys) => {
                  requestOptions(seatKeys, seating.seatingConfigurationVersionId);
                }}
                selectedSeatKeys={
                  selection.kind === 'SEATS' ? new Set(selection.venueMapSeatIds) : new Set()
                }
                onSeatToggle={(seatKey) => {
                  const option = optionByOccurrence.get(seatKey);
                  // Nothing to say about a chair the server has not described,
                  // and no guessing: an undressed chair is one whose state was
                  // never asked for, not one that happens to be free.
                  if (option === undefined) return;
                  const table = option.soldWithTableId;
                  if (table !== null) {
                    setSelection(selectWholeTable(table));
                    return;
                  }
                  if (whyNotChoosable(option) !== null) return;
                  setSelection((current) => toggleSeat(current, seatKey));
                }}
              />

              <p className="gc-muted">
                Nothing is reserved until you hold it. Somebody else can buy a seat you have
                highlighted.
              </p>
            </div>
          </Card>
        )}
      </div>
    </main>
  );
}

/**
 * Adds the physical facts to what a chair announces, and marks the ones being
 * looked for.
 *
 * Layered onto the availability dressing rather than replacing it, because they
 * answer different questions: whether a chair can be had, and what sitting in
 * it is like. A screen reader gets both in one sentence — "Seat 4, Stalls, Row
 * A, Available, Accessible seat" — which is the only way somebody not looking
 * at colours can tell them apart.
 *
 * When a customer is looking for a feature, the chairs that have it are marked
 * and the rest are left exactly as they were. Nothing is hidden: a person
 * scanning for a step-free seat still needs to see where it sits in the room,
 * and a filter that emptied the map would answer a narrower question than the
 * one they asked.
 */
/**
 * What a chair announces about the kind of place it is, and what it is linked to.
 *
 * The link is read from the authored pairs and never from the room's shape.
 * Two chairs sixty units apart in the same row may be a companion pair or may
 * be strangers; an aisle, a barrier or a step can sit between them and the
 * canvas does not know. Only the organizer who walked the room knows.
 *
 * And the companion chair describes itself, not its neighbour. Borrowing the
 * accessible place's words would tell somebody who needs a wheelchair space
 * that this chair is one.
 *
 * The wording says the chairs are linked, never that one is being kept for
 * anybody. A link is topology: the chair may already be sold, and whether it is
 * free is answered by `seat-options` like every other chair.
 */
function describeProvision(
  room: CustomerTopology,
  seat: CustomerTopology['seats'][number],
): string[] {
  const said: string[] = [];
  if (seat.accessibilityProvision !== null) {
    said.push(presentation(SEAT_ACCESSIBILITY_PROVISIONS, seat.accessibilityProvision).label);
  }

  const linkedTo = room.companions
    .filter((pair) => pair.accessibleSeatId === seat.venueMapSeatId)
    .map((pair) => room.seats.find((other) => other.venueMapSeatId === pair.companionSeatId))
    .filter((other) => other !== undefined);
  const linkedFrom = room.companions
    .filter((pair) => pair.companionSeatId === seat.venueMapSeatId)
    .map((pair) => room.seats.find((other) => other.venueMapSeatId === pair.accessibleSeatId))
    .filter((other) => other !== undefined);

  for (const other of linkedTo) {
    said.push(`linked companion seat ${other.seatLabel}`);
  }
  for (const other of linkedFrom) {
    said.push(`linked to seat ${other.seatLabel}`);
  }
  return said;
}

function dressWithFeatures(
  room: CustomerTopology,
  wanted: { accessible: boolean; unobstructed: boolean },
  availability: ReadonlyMap<string, { modifier: string; announce: string }>,
): ReadonlyMap<string, { modifier: string; announce: string }> {
  const dressed = new Map(availability);
  for (const seat of room.seats) {
    const features: string[] = [
      ...describeProvision(room, seat),
      ...(seat.hasObstructedView ? [presentation(SEAT_FEATURES, 'OBSTRUCTED_VIEW').label] : []),
    ];
    const looking = wanted.accessible || wanted.unobstructed;
    const matches =
      (!wanted.accessible || seat.isAccessible) &&
      (!wanted.unobstructed || !seat.hasObstructedView);
    if (features.length === 0 && !looking) continue;

    const base = dressed.get(seat.venueMapSeatId);
    const announce = [base?.announce, ...features].filter((part) => part !== undefined).join(', ');
    dressed.set(seat.venueMapSeatId, {
      modifier: base?.modifier ?? 'unknown',
      announce: looking && matches ? `${announce}, what you are looking for` : announce,
    });
  }
  return dressed;
}
